import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Star, CheckCircle2, Loader2, ThumbsUp, ThumbsDown, Send, Building2 } from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import type { Task } from '@/types'
import toast from 'react-hot-toast'

export default function CustomerFeedbackPage() {
  const { taskId } = useParams<{ taskId: string }>()
  const [task, setTask] = useState<Task | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Feedback form state
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null)
  const [categories, setCategories] = useState<{
    serviceQuality: number
    responseTime: number
    communication: number
    workmanship: number
  }>({
    serviceQuality: 0,
    responseTime: 0,
    communication: 0,
    workmanship: 0
  })

  useEffect(() => {
    if (!taskId) return
    loadTask()
  }, [taskId])

  const loadTask = async () => {
    setLoading(true)
    const res = await taskService.get(taskId!)
    if (res.success && res.data) {
      // Only allow feedback if task is in pending_feedback status or already has feedback
      if (res.data.status === 'pending_feedback' || res.data.status === 'closed') {
        setTask(res.data)
        // Pre-fill if already submitted
        if (res.data.customerRating) {
          setRating(res.data.customerRating)
          setFeedback(res.data.customerFeedback || '')
          setWouldRecommend(res.data.wouldRecommend ?? null)
          const cats = res.data.feedbackCategories
          if (cats?.serviceQuality && cats?.responseTime && cats?.communication && cats?.workmanship) {
            setCategories({
              serviceQuality: cats.serviceQuality,
              responseTime: cats.responseTime,
              communication: cats.communication,
              workmanship: cats.workmanship
            })
          }
          setSubmitted(true)
        }
      } else {
        toast.error('Feedback is not yet available for this ticket')
      }
    } else {
      toast.error('Ticket not found')
    }
    setLoading(false)
  }

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please provide an overall rating')
      return
    }
    if (wouldRecommend === null) {
      toast.error('Please indicate if you would recommend our service')
      return
    }
    if (categories.serviceQuality === 0 || categories.responseTime === 0 || categories.communication === 0 || categories.workmanship === 0) {
      toast.error('Please rate all service categories')
      return
    }

    setSubmitting(true)
    const res = await taskService.submitFeedback(taskId!, {
      rating,
      feedback,
      wouldRecommend,
      categories
    }, task?.customerName)

    if (res.success) {
      setSubmitted(true)
      toast.success('Thank you for your feedback! 🎉')
    } else {
      toast.error(res.error || 'Failed to submit feedback')
    }
    setSubmitting(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    )
  }

  if (!task) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🔍</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Ticket Not Found</h1>
          <p className="text-gray-600">The feedback link may be invalid or the ticket has been removed.</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
        <div className="text-center max-w-md w-full">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={40} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-black text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">Your feedback has been recorded and will help us improve our service.</p>

          <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 p-6 text-left">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">BSNL FTTH Service</p>
                <p className="text-sm font-bold text-gray-900">Ticket #{task.taskNumber}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Overall Rating</span>
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                  ))}
                </div>
              </div>
              {wouldRecommend !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Would Recommend</span>
                  <span className={`text-sm font-bold ${wouldRecommend ? 'text-emerald-600' : 'text-red-600'}`}>
                    {wouldRecommend ? 'Yes 👍' : 'No 👎'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-6">You may close this window</p>
        </div>
      </div>
    )
  }

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Building2 size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-black text-gray-900 mb-1">BSNL FTTH Service</h1>
          <p className="text-gray-500">Your feedback matters to us</p>
        </div>

        {/* Ticket Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Ticket Number</p>
              <p className="text-lg font-black text-gray-900">{task.taskNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Customer</p>
              <p className="text-sm font-bold text-gray-900">{task.customerName}</p>
            </div>
          </div>
          {task.serviceNumber && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Service Number</p>
              <p className="text-sm font-mono font-bold text-gray-700">{task.serviceNumber}</p>
            </div>
          )}
        </div>

        {/* Feedback Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 space-y-6">
            {/* Overall Rating */}
            <div className="text-center">
              <p className="text-sm font-black text-gray-900 uppercase tracking-wider mb-3">How was your overall experience?</p>
              <div className="flex items-center justify-center gap-2 mb-2">
                {Array.from({ length: 5 }).map((_, i) => {
                  const starValue = i + 1
                  return (
                    <button
                      key={i}
                      type="button"
                      onMouseEnter={() => setHoverRating(starValue)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(starValue)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        size={36}
                        className={(hoverRating || rating) >= starValue ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                      />
                    </button>
                  )
                })}
              </div>
              <p className="text-sm font-bold text-amber-600 h-5">
                {rating > 0 ? ratingLabels[rating] : 'Tap to rate'}
              </p>
            </div>

            {/* Category Ratings */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-black text-gray-400 uppercase tracking-wider">Rate Specific Areas</p>

              {[
                { key: 'serviceQuality', label: 'Service Quality' },
                { key: 'responseTime', label: 'Response Time' },
                { key: 'communication', label: 'Communication' },
                { key: 'workmanship', label: 'Workmanship' }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <div className="flex gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setCategories(prev => ({ ...prev, [key]: i + 1 }))}
                        className="p-0.5 transition-transform hover:scale-110"
                      >
                        <Star
                          size={20}
                          className={categories[key as keyof typeof categories] > i ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Would Recommend */}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Would you recommend our service?</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setWouldRecommend(true)}
                  className={`flex-1 py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    wouldRecommend === true
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-emerald-300 text-gray-600'
                  }`}
                >
                  <ThumbsUp size={16} /> Yes
                </button>
                <button
                  type="button"
                  onClick={() => setWouldRecommend(false)}
                  className={`flex-1 py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    wouldRecommend === false
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 hover:border-red-300 text-gray-600'
                  }`}
                >
                  <ThumbsDown size={16} /> No
                </button>
              </div>
            </div>

            {/* Written Feedback */}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-2">
                Additional Comments <span className="text-gray-300 font-normal">(optional)</span>
              </p>
              <textarea
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="Tell us about your experience..."
                rows={4}
                className="w-full text-sm border border-gray-200 rounded-xl p-3 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <button
              onClick={handleSubmit}
              disabled={submitting || rating === 0 || wouldRecommend === null}
              className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-black rounded-xl shadow-md active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Submitting...
                </>
              ) : (
                <>
                  <Send size={18} /> Submit Feedback
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          BSNL Fiber to the Home (FTTH) Service<br />
          Your feedback helps us serve you better
        </p>
      </div>
    </div>
  )
}
